import { Trend } from "ExtendiApp"
import React, { useState, useEffect, Fragment } from "react"

import {
  Nav,
  NavItem,
  NavLink,
  Dropdown,
  DropdownMenu,
  DropdownToggle,
  DropdownItem,
} from "src/ui"

interface Props {
  trends: Trend[]
  onCategoryChange: (newCategory: string | null) => void
  categoriesReferenceIdOrder: string[]
}
type Categories = {
  [key: string]: {
    id: string
    displayName: string
    trends: string[]
  }
}

type ActiveCategory = string | null

export const CategorySelector: React.FC<Props> = ({
  trends,
  categoriesReferenceIdOrder,
  onCategoryChange,
}) => {
  const [activeCategory, setActiveCategory] = useState<ActiveCategory>(null)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const toggle = () => setDropdownOpen(prevState => !prevState)

  useEffect(() => {
    onCategoryChange(activeCategory)
  }, [activeCategory])

  const categoriesWithTrends = trends.reduce((categories, trend) => {
    const trendCategories = trend.categories.reduce(
      (trendCategories, { id, displayName }) => ({
        ...trendCategories,
        [id]: {
          id,
          displayName,
          trends: [...(categories[id]?.trends || []), trend.topic],
        },
      }),
      {} as Categories
    )

    return {
      ...categories,
      ...trendCategories,
    }
  }, {} as Categories)

  const handleClick = (
    e: React.MouseEvent<{}, MouseEvent>,
    categoryToSetAsActive: ActiveCategory
  ) => {
    e.preventDefault()
    setActiveCategory(categoryToSetAsActive)
  }

  const categoryIdsToShow = Object.keys(categoriesWithTrends).filter(
    key => categoriesWithTrends[key].trends?.length > 2
  )

  const sortedCategories =
    categoriesReferenceIdOrder && categoriesReferenceIdOrder.length
      ? [...categoryIdsToShow].sort((a, b) =>
          categoriesReferenceIdOrder.indexOf(a) >
          categoriesReferenceIdOrder.indexOf(b)
            ? 1
            : -1
        )
      : []

  return (
    <Fragment>
      {/* NAV PILLS FOR DESKTOP */}
      <div className="d-none d-md-block">
        <Nav className="d-flex justify-content-center" pills>
          <NavItem>
            <NavLink
              href="#"
              active={!activeCategory}
              onClick={e => {
                handleClick(e, null)
              }}
            >
              ALL
            </NavLink>
          </NavItem>
          {sortedCategories.map(catId => (
            <NavItem key={catId}>
              <NavLink
                href="#"
                onClick={e => {
                  handleClick(e, catId)
                }}
                active={activeCategory === catId}
              >
                {categoriesWithTrends[catId].displayName}
              </NavLink>
            </NavItem>
          ))}
        </Nav>
      </div>

      {/* DROPDOWN FOR MOBILE */}
      <div className="d-md-none">
        <Dropdown isOpen={dropdownOpen} toggle={toggle}>
          <DropdownToggle
            caret
            color="primary"
            size="lg"
            className="w-100"
            data-flip="false"
          >
            Category:{" "}
            {activeCategory
              ? categoriesWithTrends[activeCategory].displayName
              : "ALL"}
          </DropdownToggle>
          <DropdownMenu className="w-100 shadow">
            <DropdownItem
              onClick={e => handleClick(e, null)}
              className={!activeCategory ? "active" : ""}
            >
              ALL
            </DropdownItem>
            {sortedCategories.map(catId => (
              <DropdownItem
                key={catId}
                onClick={e => {
                  handleClick(e, catId)
                }}
                className={activeCategory === catId ? "active" : ""}
              >
                {categoriesWithTrends[catId].displayName}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </Dropdown>
      </div>
    </Fragment>
  )
}
